import Cookies from 'js-cookie';
import { getTenantById } from 'tenants/manager';
import type { TenantId } from 'tenants/types/tenant';
import { LOCATION_EXPIRE_DAYS } from 'constants/cookies';
import { getLocalizedUrl } from './get-localized-url';

/**
 * Centralized locale management system.
 * 
 * 1. Cookie preference takes highest priority (user's explicit choice)
 * 2. URL locale used if no cookie preference exists
 * 3. When user changes locale: save cookie + navigate to new URL
 * 4. Fallback: tenant default locale
 */
export class LocaleManager {
  private static readonly COOKIE_NAME = 'locale-preference';

  constructor(
    private tenantId: TenantId,
    private routeLocale?: string,
    private serverCookies?: string
  ) {}

  /**
   * Get the current locale prioritizing user preference over URL route.
   * Cookie preference takes priority over URL route for persistence like realtor.ca.
   */
  getCurrentLocale(): string {
    const tenant = getTenantById(this.tenantId);
    
    // First check for saved user preference (cookie priority)
    const savedPreference = this.getSavedPreference();
    if (savedPreference && tenant.localization.languages.includes(savedPreference)) {
      return savedPreference;
    }
    
    // Fall back to route locale if valid
    if (this.routeLocale && tenant.localization.languages.includes(this.routeLocale)) {
      return this.routeLocale;
    }
    
    // Finally use tenant default
    return tenant.localization.defaultLanguage;
  }

  /**
   * Get the user's saved locale preference from cookies.
   * This doesn't affect what locale is currently active - it's just the saved preference.
   */
  getSavedPreference(): string | null {
    // Server-side: parse from request headers
    if (typeof window === 'undefined') {
      if (!this.serverCookies) return null;
      
      const cookies = this.serverCookies.split(';');
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === LocaleManager.COOKIE_NAME) {
          return decodeURIComponent(value);
        }
      }
      return null;
    }
    
    // Client-side: use js-cookie
    return Cookies.get(LocaleManager.COOKIE_NAME) || null;
  }

  /**
   * Save user's locale preference to cookies.
   * This doesn't change the current locale - just saves the preference.
   */
  async savePreference(locale: string): Promise<void> {
    const tenant = getTenantById(this.tenantId);
    
    if (!tenant.localization.languages.includes(locale)) {
      throw new Error(`Locale ${locale} not supported for tenant ${this.tenantId}`);
    }

    // Only save on client-side
    if (typeof window === 'undefined') return;

    Cookies.set(LocaleManager.COOKIE_NAME, locale, {
      expires: LOCATION_EXPIRE_DAYS,
      path: '/',
      secure: true,
      sameSite: 'lax',
    });
  }

  /**
   * Navigate to a new locale.
   * This saves the preference and navigates to the new URL.
   */
  async switchToLocale(locale: string, currentPath: string): Promise<void> {
    const tenant = getTenantById(this.tenantId);
    
    if (tenant.localization.persistLocalePreference) {      
      await this.savePreference(locale);
    }
    
    // Navigate to new URL
    const newUrl = getLocalizedUrl({
      locale,
      tenantId: this.tenantId,
      path: currentPath,
    });
    
    // Use full page navigation for reliability
    window.location.href = newUrl;
  }

  /**
   * Check if locale switching is enabled for this tenant.
   */
  isLocalesSwitchingEnabled(): boolean {
    const tenant = getTenantById(this.tenantId);
    return tenant.localization.languages.length > 1 && 
           (tenant.localization.switchingEnabled ?? false);
  }

  /**
   * Get available locales for this tenant.
   */
  getAvailableLocales(): readonly string[] {
    const tenant = getTenantById(this.tenantId);
    return tenant.localization.languages;
  }

  /**
   * Get the tenant's default locale.
   */
  getDefaultLocale(): string {
    const tenant = getTenantById(this.tenantId);
    return tenant.localization.defaultLanguage;
  }

  /**
   * Create a locale manager for server-side use.
   */
  static forServer(tenantId: TenantId, routeLocale?: string, requestCookies?: string): LocaleManager {
    return new LocaleManager(tenantId, routeLocale, requestCookies);
  }

  /**
   * Create a locale manager for client-side use.
   */
  static forClient(tenantId: TenantId, routeLocale?: string): LocaleManager {
    return new LocaleManager(tenantId, routeLocale);
  }
}
